import type { Job } from "../types/job";

// "question-batch-generate" job'ı: sahne yerine üretim ilerlemesi + üretilen sorular gösterilir.
// job.type ya da metadata.type üzerinden yakala (backend ikisinden birinde yollayabiliyor).
const norm = (s?: string | null) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, "-");

export const isQuestionBatchType = (job: Job): boolean =>
  norm(job.type) === "question-batch-generate" ||
  norm(job?.metadata?.type) === "question-batch-generate";

// Cevap/soru metinleri BasicText plugin'inde HTML (<p>…</p>) olarak geliyor.
const stripHtml = (s?: string | null): string =>
  (s || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();

export type QuestionKind =
  | "MultipleChoice"
  | "MultipleAnswer"
  | "TrueFalse"
  | "Ordering"
  | "DragDrop"
  | "FreeResponse"
  | "FillInBlank"
  | "Matching"
  | "Unknown";

export interface ParsedAnswer {
  text: string;
  correct: boolean; // MC/MA: doğru şık; TrueFalse: ifadenin doğruluk değeri
  order: number | null; // Ordering/DragDrop: doğru sıradaki konum (1 tabanlı)
}
export interface MatchPair {
  left: string;
  right: string;
}
export interface ParsedQuestion {
  kind: QuestionKind;
  question: string;
  answers: ParsedAnswer[];
  audioUrl: string | null;
  blanks?: string[]; // FillInBlank: doğru cevap(lar)
  pairs?: MatchPair[]; // Matching: doğru eşleşmeler
}

// Backend şekli: her soruda pluginToolbarsById (soru tipi + BasicText kutuları),
// boxesById (kutu container'ları → sc-Question/sc-AnswerN/sc-*Feedback), correctAnswers map'i,
// questionText ve narrationAudioUrl bulunur.
function parseQuestion(q: any): ParsedQuestion {
  const toolbars: Record<string, any> = q?.pluginToolbarsById || {};
  const boxes: Record<string, any> = q?.boxesById || {};

  // Kök plugin kutusu = pluginId'si BasicText olmayan tek kutu → soru tipini verir
  let rootId = "";
  let kind: QuestionKind = "Unknown";
  let rootState: any = {};
  for (const [id, tb] of Object.entries(toolbars)) {
    if (tb?.pluginId && tb.pluginId !== "BasicText") {
      rootId = id;
      kind = tb.pluginId as QuestionKind;
      rootState = tb.state || {};
      break;
    }
  }

  const question = stripHtml(q?.questionText) || stripHtml(toolbars[`${rootId}-q`]?.state?.__text);

  // Cevap kutuları: boxesById container'ı sc-Answer<idx> olanlar (MC/TF/Ordering: -a*, Matching: -l*/-r*)
  const answerEntries = Object.keys(toolbars)
    .filter((id) => toolbars[id]?.pluginId === "BasicText")
    .map((id) => ({ id, m: /^sc-Answer(\d+)$/.exec(boxes[id]?.container || "") }))
    .filter((e) => e.m)
    .map((e) => ({ idx: Number(e.m![1]), text: stripHtml(toolbars[e.id]?.state?.__text) }))
    .sort((a, b) => a.idx - b.idx);

  const ca = (q?.correctAnswers || {})[rootId];
  const answers: ParsedAnswer[] = answerEntries.map((a, i) => {
    let correct = false;
    let order: number | null = null;
    if (kind === "MultipleChoice") correct = ca === i;
    else if (kind === "MultipleAnswer") correct = Array.isArray(ca) && ca.includes(i);
    else if (kind === "TrueFalse") correct = Array.isArray(ca) ? !!ca[i] : false;
    else if (kind === "Ordering" || kind === "DragDrop") {
      if (Array.isArray(ca)) {
        const pos = ca.indexOf(i);
        order = pos >= 0 ? pos + 1 : null;
      }
    }
    return { text: a.text, correct, order };
  });

  // FillInBlank: correctAnswers[rootId] = ["para"] gibi doğru metin(ler)
  const blanks =
    kind === "FillInBlank" && Array.isArray(ca) ? ca.map((x: any) => String(x)) : undefined;

  // Matching: correctAnswers[rootId] = [[li, ri], …]; sol grup ilk nBoxes şık, sağ grup kalanlar
  let pairs: MatchPair[] | undefined;
  if (kind === "Matching" && Array.isArray(ca)) {
    const leftCount = Number(rootState.nBoxes) || Math.floor(answers.length / 2);
    const left = answers.slice(0, leftCount);
    const right = answers.slice(leftCount);
    pairs = ca
      .filter((p: any) => Array.isArray(p))
      .map((p: any) => ({
        left: left[p[0]]?.text || "",
        right: right[p[1]]?.text || "",
      }));
  }

  return { kind, question, answers, audioUrl: q?.narrationAudioUrl || null, blanks, pairs };
}

export function extractQuestions(job: Job): ParsedQuestion[] {
  const list = [
    job?.result?.questions,
    job?.metadata?.questions,
    job?.data?.questions,
    (job as any)?.questions,
  ].find(Array.isArray) as any[] | undefined;
  if (!list) return [];
  return list
    .map(parseQuestion)
    .filter((q) => q.question || q.answers.length > 0 || q.blanks || q.pairs);
}

export interface QuestionBatchInfo {
  total: number;
  progress: number;
  done: boolean;
  questions: ParsedQuestion[];
}

export function getQuestionBatchInfo(job: Job): QuestionBatchInfo {
  const m = (job.metadata ?? {}) as any;
  const r = (job.result ?? {}) as any;
  const d = (job.data ?? {}) as any;
  const total = Number(r.total ?? m.total ?? (Array.isArray(d.items) ? d.items.length : 0)) || 0;
  const progress = Number(job.progress ?? m.progress ?? 0) || 0;
  const questions = extractQuestions(job);
  const st = (job.status || m.status || "").toLowerCase();
  const done =
    st === "completed" ||
    st === "inspect" ||
    st === "done" ||
    progress >= 100 ||
    questions.length > 0;
  return { total, progress, done, questions };
}
